import React, { useState } from "react";

import { IUserDocument } from "../../Models/IUserDocument";

// Internationalization Import
import i18n from "i18next";
import { IDokument2Dokumentgruppe } from "../../Models/IDokument2Dokumentegruppe";
import { baseUrl } from "../../helpers/constants";
import { IUserDocument2SpracheDto } from "../../Models/IUserDocument2Sprache";
import {
  Button,
  createTableColumn,
  Link,
  Text,
  Menu,
  MenuItem,
  MenuList,
  MenuPopover,
  MenuTrigger,
  Tooltip,
} from "@fluentui/react-components";
import { StackShim } from "@fluentui/react-migration-v8-v9";
import {
  ArrowDownloadRegular,
  DeleteRegular,
  DocumentCube24Regular,
  DocumentPdf24Regular,
  FilmstripPlay24Regular,
  KeyRegular,
  LinkRegular,
  MoreVerticalRegular,
  PeopleTeamRegular,
} from "@fluentui/react-icons";

export function MediathekColumns() {
  const [documentToDownload, setDocumentToDownload] = useState<
    IUserDocument | undefined
  >(undefined);

  const [documentToView, setDocumentToView] = useState<
    IUserDocument | undefined
  >(undefined);

  const [documentToDelete, setDocumentToDelete] = useState<
    IUserDocument | undefined
  >(undefined);

  const [documentToShare, setDocumentToShare] = useState<
    IUserDocument | undefined
  >(undefined);

  const [shortLink, setShortLink] = useState<string | undefined>(undefined);

  const columns = [
    // ------------Extension Bild------------
    createTableColumn<IUserDocument>({
      columnId: "extensionId",
      renderHeaderCell: () => {
        return <Text> {i18n.t<string>("docFileExtension")}</Text>;
      },
      renderCell: (item) => {
        return (
          <div className="lueb-centerVertical">
            <Tooltip content={item.docFileExtension} relationship={"label"}>
              <div>
                {(item.docFileExtension == ".PDF" ||
                  item.docFileExtension == "PDF") && <DocumentPdf24Regular />}
                {item.docFileExtension == ".STP" && <DocumentCube24Regular />}
                {(item.docFileExtension == "MP4" ||
                  item.docFileExtension == ".MP4") && (
                  <FilmstripPlay24Regular />
                )}
              </div>
            </Tooltip>
          </div>
        );
      },
    }),

    // ------------Dateiname------------
    createTableColumn<IUserDocument>({
      columnId: "nameId",
      renderHeaderCell: () => {
        return <Text> {i18n.t<string>("FileName")}</Text>;
      },
      renderCell: (item) => {
        return (
          <div className="lueb-centerVertical">
            <StackShim>
              <Link
                onClick={() => {
                  if (
                    item.docFileExtension.toLocaleLowerCase().includes("stp")
                  ) {
                    setDocumentToDownload(item);
                  } else {
                    setDocumentToView(item);
                  }
                }}
              >
                {item.displayDocFileName
                  ? item.displayDocFileName
                  : item.docFileName}
              </Link>
              {item.description && <Text>{item.description}</Text>}
              {item.artikel && (
                <Text>
                  {item.artikel.artikelId} {" - "}
                  {i18n.language.includes("de")
                    ? item.artikel.benennungDE
                    : item.artikel.benennungEN}
                </Text>
              )}
              {item.produkt && (
                <Text>
                  {item.produkt.produktId} {" - "}
                  {i18n.language.includes("de")
                    ? item.produkt?.artikel?.benennungDE
                    : item.produkt?.artikel?.benennungEN}
                </Text>
              )}
            </StackShim>
          </div>
        );
      },
    }),

    // ------------Actions------------
    createTableColumn<IUserDocument>({
      columnId: "actionsId",
      renderCell: (item) => {
        const menuItems: any[] = [
          {
            key: "download",
            icon: <ArrowDownloadRegular />,
            text: i18n.t<string>("download"),
            onClick: () => {
              setDocumentToDownload(item);
            },
          },
        ];

        if (item.canDeleteObject) {
          menuItems.push({
            key: "deleteDoc",
            icon: <DeleteRegular />,
            text: i18n.t<string>("delete"),
            onClick: () => {
              setDocumentToDelete(item);
            },
          });
        }

        if (item.canCreateFreigabe) {
          menuItems.push({
            key: "createFreigabe",
            icon: item.hasFreigaben ? <PeopleTeamRegular /> : <KeyRegular />,

            text: i18n.t<string>("shareObject"),
            onClick: () => {
              setDocumentToShare(item);
            },
          });
        }

        if (item.shortIdent) {
          menuItems.push({
            key: "shortIdent",
            icon: <LinkRegular />,
            text: i18n.t<string>("generateShortLink"),
            onClick: () => {
              setShortLink(baseUrl + "/mediathek?id=" + item.shortIdent);
            },
          });
        }

        if (menuItems.length > 1) {
          return (
            <div className="lueb-centerVertical">
              <Menu>
                <MenuTrigger>
                  <Button
                    appearance="transparent"
                    icon={<MoreVerticalRegular />}
                  />
                </MenuTrigger>
                <MenuPopover>
                  <MenuList>
                    {menuItems.map((item) => {
                      return (
                        <MenuItem
                          onClick={() => {
                            item.onClick();
                          }}
                          icon={item.icon}
                        >
                          {item.text}
                        </MenuItem>
                      );
                    })}
                  </MenuList>
                </MenuPopover>
              </Menu>
            </div>
          );
        } else {
          return (
            <div className="lueb-centerVertical">
              <Tooltip
                content={i18n.t<string>("download")}
                relationship={"label"}
              >
                <Button
                  appearance="transparent"
                  icon={<ArrowDownloadRegular />}
                  onClick={() => {
                    setDocumentToDownload(item);
                  }}
                />
              </Tooltip>
            </div>
          );
        }
      },
    }),

    // ------------Dokument Datum------------
    createTableColumn<IUserDocument>({
      columnId: "docDateId",
      renderHeaderCell: () => {
        return <Text> {i18n.t<string>("date")}</Text>;
      },
      renderCell: (item) => {
        return (
          <div className="lueb-centerVertical">
            <Text>
              {item.docDate ? new Date(item.docDate).toLocaleDateString() : ""}
            </Text>
          </div>
        );
      },
    }),

    // ------------Sprachen------------
    createTableColumn<IUserDocument>({
      columnId: "sprachenId",
      renderHeaderCell: () => {
        return <Text> {i18n.t<string>("languages")}</Text>;
      },
      renderCell: (item) => {
        return (
          <div className="lueb-centerVertical">
            <StackShim>
              {item.dokumentSprachen?.map(
                (docSprache: IUserDocument2SpracheDto) => {
                  return (
                    <Text>
                      {i18n.language.includes("de")
                        ? docSprache.sprache?.nameDE
                        : docSprache.sprache?.nameEN}
                    </Text>
                  );
                },
              )}
            </StackShim>
          </div>
        );
      },
    }),

    // ------------Dokumentengruppe------------
    createTableColumn<IUserDocument>({
      columnId: "docGroupId",
      renderHeaderCell: () => {
        return <Text> {i18n.t<string>("docGroup")}</Text>;
      },
      renderCell: (item) => {
        return item.dokument2Dokumentgruppen?.map(
          (docGroup: IDokument2Dokumentgruppe) => {
            return (
              <div className="lueb-centerVertical">
                <Text>
                  {i18n.language.includes("de")
                    ? docGroup.dokumentgruppe?.nameDE
                    : docGroup.dokumentgruppe?.nameEN}
                </Text>
              </div>
            );
          },
        );
      },
    }),
    // ------------Verfügbarkeitsdatum------------
    createTableColumn<IUserDocument>({
      columnId: "validUntil",
      renderHeaderCell: () => {
        return <Text> {i18n.t<string>("validUntil")}</Text>;
      },
      renderCell: (item) => {
        return (
          <div className="lueb-centerVertical">
            {item.validUntil &&
            new Date(item.validUntil).toLocaleDateString() != "1.1.1"
              ? new Date(item.validUntil).toLocaleDateString()
              : "-"}
          </div>
        );
      },
    }),
  ];

  return {
    shortLink: shortLink,
    documentToDelete: documentToDelete,
    documentToShare: documentToShare,
    documentToDownload: documentToDownload,
    documentToView: documentToView,
    resetShortLink: () => setShortLink(undefined),
    resetDocumentToDownload: () => setDocumentToDownload(undefined),
    resetDocumentToView: () => setDocumentToView(undefined),
    resetDocumentToDelete: () => setDocumentToDelete(undefined),
    resetDocumentToShare: () => setDocumentToShare(undefined),
    columns: columns,
  };
}
